import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { SettingsContext } from '../contexts/SettingsContext';
import TournamentBracket from '../components/TournamentBracket';
import { QRCodeSVG } from 'qrcode.react';

function PresentationView() {
  const [activeTournaments, setActiveTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [rankings, setRankings] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [tournamentsLoaded, setTournamentsLoaded] = useState(false);
  const [error, setError] = useState('');
  const [currentView, setCurrentView] = useState('standings'); // 'standings', 'matches', 'schedule', 'rankings'
  const [fadeClass, setFadeClass] = useState('fade-in');
  const [progress, setProgress] = useState(0);
  const [qrLangDe, setQrLangDe] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  // Settings aus Context laden
  const { settings } = useContext(SettingsContext);
  const logoUrl = settings.logoUrl || 'https://x3padel.de/wp-content/uploads/2025/09/horizontal-black-portreslogo.svg';
  const showSponsor = settings.showSponsor;
  const sponsors = settings.sponsors || [];
  const slideDuration = (settings.slideDuration || 10) * 1000;
  const qrUrl = settings.qrUrl || window.location.origin;

  const [currentSponsorIndex, setCurrentSponsorIndex] = useState(0);
  const currentSponsor = showSponsor && sponsors.length > 0 ? sponsors[currentSponsorIndex % sponsors.length] : null;

  const currentViewIndexRef = useRef(0);
  const currentTournamentIndexRef = useRef(0);
  const socketRef = useRef(null);
  const schedulePageCountRef = useRef(1);
  const SCHEDULE_ITEMS_PER_PAGE = 15;

  // Auto-Scale für Turnierbaum
  const bracketContainerRef = useRef(null);
  const bracketContentRef = useRef(null);
  const [bracketScale, setBracketScale] = useState(1);

  // Dark mode state - synchronisiert mit Hauptseite
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Lese Theme aus localStorage (wie Hauptseite)
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Fallback: System preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Überwache Theme-Änderungen von der Hauptseite
  useEffect(() => {
    const checkTheme = () => {
      const dataTheme = document.documentElement.getAttribute('data-theme');
      const savedTheme = localStorage.getItem('theme');
      const theme = dataTheme || savedTheme;
      setIsDarkMode(theme === 'dark');
    };

    // Initial check
    checkTheme();

    // Beobachte data-theme Attribut Änderungen
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          checkTheme();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    // Storage event für Tab-übergreifende Synchronisation
    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        setIsDarkMode(e.newValue === 'dark');
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      observer.disconnect();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Auto-Scale Turnierbaum: passt Bracket an verfügbare Containergröße an
  useEffect(() => {
    const container = bracketContainerRef.current;
    const content   = bracketContentRef.current;
    if (!container || !content) return;

    const recalc = () => {
      // scrollWidth/Height = natürliche Größe (unabhängig von transform)
      const cW = container.clientWidth;
      const cH = container.clientHeight;
      const bW = content.scrollWidth;
      const bH = content.scrollHeight;
      if (!bW || !bH || !cW || !cH) return;
      const scale = Math.min(cW / bW, cH / bH, 1);
      setBracketScale(scale);
    };

    const ro = new ResizeObserver(recalc);
    ro.observe(container);
    ro.observe(content);
    const t = setTimeout(recalc, 80); // initial nach DOM-Aufbau
    return () => { ro.disconnect(); clearTimeout(t); };
  }, [tournament, matches, teams]);

  // Lade alle aktiven Turniere
  useEffect(() => {
    const fetchActiveTournaments = async () => {
      try {
        const response = await axios.get('/api/tournaments/active/all');
        const visible = response.data.filter(t => t.show_in_presentation !== 0);
        setActiveTournaments(visible);
        setTournamentsLoaded(true);

        if (!selectedTournamentId && visible.length > 0) {
          setSelectedTournamentId(visible[0].id);
        }

        // Wenn keine Turniere vorhanden, Loading beenden
        if (response.data.length === 0) {
          setLoading(false);
        }
      } catch (err) {
        console.error('Fehler beim Laden der aktiven Turniere:', err);
        setError('Fehler beim Laden der Turniere');
        setTournamentsLoaded(true);
        setLoading(false);
      }
    };

    fetchActiveTournaments();
    const interval = setInterval(fetchActiveTournaments, 30000);
    return () => clearInterval(interval);
  }, [selectedTournamentId]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    socketRef.current = io(apiUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('connect', () => {
      console.log('WebSocket connected');
      setWsConnected(true);
    });

    socketRef.current.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
    });

    socketRef.current.on('match_updated', (data) => {
      console.log('Match updated via WebSocket:', data);
      // Update local matches state with new match data
      setMatches((prevMatches) =>
        prevMatches.map((match) =>
          match.id === data.match_id
            ? { ...match, ...data }
            : match
        )
      );
    });

    socketRef.current.on('round_generated', (data) => {
      console.log('New round generated via WebSocket:', data);
      // Lade Matches neu wenn eine neue Runde generiert wurde
      if (data.tournament_id) {
        axios.get(`/api/tournaments/${data.tournament_id}/matches`)
          .then(response => {
            setMatches(response.data);
            console.log('Matches reloaded after new round');
          })
          .catch(err => {
            console.error('Failed to reload matches:', err);
          });
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Join/leave tournament room when tournament changes
  useEffect(() => {
    if (!selectedTournamentId || !socketRef.current) return;

    socketRef.current.emit('join_tournament', selectedTournamentId);
    console.log(`Joined tournament room: ${selectedTournamentId}`);

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_tournament', selectedTournamentId);
        console.log(`Left tournament room: ${selectedTournamentId}`);
      }
    };
  }, [selectedTournamentId]);

  // Lade Turnierdaten
  useEffect(() => {
    const fetchTournamentData = async () => {
      if (!selectedTournamentId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const currentTournament = activeTournaments.find(t => t.id === selectedTournamentId);
        setTournament(currentTournament);

        const teamsResponse = await axios.get(`/api/tournaments/${selectedTournamentId}/teams`);
        let teamsData = teamsResponse.data;

        const matchesResponse = await axios.get(`/api/tournaments/${selectedTournamentId}/matches`);
        const matchesData = matchesResponse.data;

        // Berechne Punkte für Groups-Turniere
        if (currentTournament?.tournament_type === 'groups') {
          teamsData = teamsData.map(team => {
            const teamMatches = matchesData.filter(m =>
              (m.team1_id === team.id || m.team2_id === team.id) &&
              m.status === 'beendet'
            );

            let points = 0, wins = 0, losses = 0, draws = 0;
            let goalsFor = 0, goalsAgainst = 0;

            teamMatches.forEach(match => {
              const isTeam1 = match.team1_id === team.id;

              // Check if it's a best-of-3 match (has set scores)
              if (match.team1_set1 !== null) {
                // Best-of-3: Count won sets
                let team1Sets = 0;
                let team2Sets = 0;

                if (match.team1_set1 > match.team2_set1) team1Sets++; else team2Sets++;
                if (match.team1_set2 > match.team2_set2) team1Sets++; else team2Sets++;
                if (match.team1_set3 !== null) {
                  if (match.team1_set3 > match.team2_set3) team1Sets++; else team2Sets++;
                }

                const ownSets = isTeam1 ? team1Sets : team2Sets;
                const oppSets = isTeam1 ? team2Sets : team1Sets;

                goalsFor += ownSets;
                goalsAgainst += oppSets;

                if (ownSets > oppSets) {
                  wins++;
                  points += 3;
                } else if (ownSets < oppSets) {
                  losses++;
                } else {
                  draws++;
                  points += 1;
                }
              } else if (match.team1_score !== null) {
                // Regular match with single score
                const ownScore = isTeam1 ? match.team1_score : match.team2_score;
                const oppScore = isTeam1 ? match.team2_score : match.team1_score;

                goalsFor += ownScore;
                goalsAgainst += oppScore;

                if (ownScore > oppScore) {
                  wins++;
                  points += 3;
                } else if (ownScore < oppScore) {
                  losses++;
                } else {
                  draws++;
                  points += 1;
                }
              }
            });

            return {
              ...team,
              points,
              wins,
              losses,
              draws,
              goals_for: goalsFor,
              goals_against: goalsAgainst
            };
          });
        }

        setTeams(teamsData);
        setMatches(matchesData);

        // Load schedule if available (groups and ko with start_time)
        if ((currentTournament?.tournament_type === 'groups' || currentTournament?.tournament_type === 'ko_placement') && currentTournament?.start_time) {
          try {
            const scheduleResponse = await axios.get(`/api/tournaments/${selectedTournamentId}/schedule`);
            if (scheduleResponse.data.schedule) {
              setSchedule(scheduleResponse.data.schedule);
            }
          } catch (err) {
            console.error('Fehler beim Laden des Zeitplans:', err);
          }
        }

        // Load rankings if tournament is completed
        if (currentTournament?.is_completed === 1 && currentTournament?.final_rankings) {
          setRankings(currentTournament.final_rankings);
        } else {
          setRankings(null);
        }

        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Turnierdaten:', err);
        setError('Fehler beim Laden der Turnierdaten');
      } finally {
        setLoading(false);
      }
    };

    if (selectedTournamentId) {
      fetchTournamentData();
      const interval = setInterval(fetchTournamentData, 30000); // Fallback: sync every 30s
      return () => clearInterval(interval);
    }
  }, [selectedTournamentId, activeTournaments]);

  // Aktualisiere Schedule-Seitenanzahl wenn Schedule geladen wird
  useEffect(() => {
    if (!schedule.length) {
      schedulePageCountRef.current = 1;
      return;
    }
    schedulePageCountRef.current = Math.max(1, Math.ceil(schedule.length / SCHEDULE_ITEMS_PER_PAGE));
  }, [schedule, SCHEDULE_ITEMS_PER_PAGE]);

  // Ref für aktuelle Anzahl Sponsoren (vermeidet stale closure im Interval)
  const sponsorsLengthRef = useRef(sponsors.length);
  useEffect(() => { sponsorsLengthRef.current = sponsors.length; }, [sponsors.length]);

  // Auto-Rotation zwischen Views UND Turnieren
  useEffect(() => {
    if (activeTournaments.length === 0) return;

    const currentTournament = activeTournaments.find(t => t.id === selectedTournamentId);

    // Views je nach Turniertyp und Status
    const isGroups = currentTournament?.tournament_type === 'groups';
    const hasKOPhase = isGroups && currentTournament?.group_phase_completed === 1;
    const isCompleted = currentTournament?.is_completed === 1;

    const viewDuration = slideDuration;

    // Progress Bar Update
    setProgress(0);
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const increment = 100 / (viewDuration / 100);
        const newProgress = prev + increment;
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, 100);

    const buildViews = () => {
      const schedulePages = Array.from({ length: schedulePageCountRef.current }, (_, i) => `schedule_${i}`);
      if (isCompleted) {
        return hasKOPhase
          ? ['standings', 'bracket', ...schedulePages, 'rankings']
          : ['standings', ...schedulePages, 'rankings'];
      } else {
        return hasKOPhase
          ? ['standings', 'bracket', ...schedulePages]
          : ['standings', ...schedulePages];
      }
    };

    const rotationInterval = setInterval(() => {
      setFadeClass('fade-out');

      setTimeout(() => {
        currentViewIndexRef.current++;

        // Views frisch berechnen, damit neue Schedule-Seiten berücksichtigt werden
        const freshViews = buildViews();

        // Nach allen Views, wechsle zum nächsten Turnier
        if (currentViewIndexRef.current >= freshViews.length) {
          currentViewIndexRef.current = 0;
          currentTournamentIndexRef.current = (currentTournamentIndexRef.current + 1) % activeTournaments.length;
          setSelectedTournamentId(activeTournaments[currentTournamentIndexRef.current].id);
        }

        setCurrentView(freshViews[currentViewIndexRef.current]);
        setQrLangDe(prev => !prev);

        // Nächsten Sponsor anzeigen (synchron mit Slide-Wechsel)
        setCurrentSponsorIndex(prev => (prev + 1) % Math.max(1, sponsorsLengthRef.current));

        // Reset progress
        setProgress(0);

        // Kleiner Delay vor dem Fade-In um sicherzustellen dass der neue Content geladen ist
        setTimeout(() => {
          setFadeClass('fade-in');
        }, 50);
      }, 400); // Fade-out Dauer (muss mit CSS Animation übereinstimmen)
    }, viewDuration);

    return () => {
      clearInterval(rotationInterval);
      clearInterval(progressInterval);
    };
  }, [activeTournaments, selectedTournamentId, slideDuration]);

  // Hilfsfunktion für Spielernamen
  const getDisplayName = (team) => {
    if (!team) return 'TBD';
    if (tournament?.tournament_type === 'mexicano' || tournament?.tournament_type === 'americano') {
      return team.players?.[0]?.name || team.name;
    }
    return team.players?.map(p => p.name).join(' / ') || team.name;
  };

  // Rendere Rangliste für Mexicano/Americano
  const renderStandings = () => {
    if (!tournament || !teams.length) return null;
    if (tournament.tournament_type === 'groups') return renderGroupStandings();
    if (tournament.tournament_type === 'ko_placement') return renderKOBracket();
    // mexicano / americano: Punktetabelle

    const computeH2H = (teamAId, teamBId) => {
      let aPoints = 0, bPoints = 0;
      matches.filter(m => m.status === 'beendet').forEach(match => {
        const aOnSide1 = match.team1_id === teamAId || match.team3_id === teamAId;
        const aOnSide2 = match.team2_id === teamAId || match.team4_id === teamAId;
        const bOnSide1 = match.team1_id === teamBId || match.team3_id === teamBId;
        const bOnSide2 = match.team2_id === teamBId || match.team4_id === teamBId;
        if ((aOnSide1 && bOnSide2) || (aOnSide2 && bOnSide1)) {
          if (match.team1_score != null && match.team2_score != null) {
            if (aOnSide1) { aPoints += match.team1_score; bPoints += match.team2_score; }
            else { aPoints += match.team2_score; bPoints += match.team1_score; }
          }
        }
      });
      return aPoints - bPoints;
    };
    // Precompute goalDiff für Sortierung
    const teamsWithDiff = teams.map(team => {
      const tm = matches.filter(m =>
        (m.team1_id === team.id || m.team2_id === team.id || m.team3_id === team.id || m.team4_id === team.id) &&
        m.team1_score != null && m.team2_score != null
      );
      let goalsFor = 0, goalsAgainst = 0;
      tm.forEach(match => {
        const isTeam1Side = match.team1_id === team.id || match.team3_id === team.id;
        if (isTeam1Side) { goalsFor += match.team1_score || 0; goalsAgainst += match.team2_score || 0; }
        else { goalsFor += match.team2_score || 0; goalsAgainst += match.team1_score || 0; }
      });
      return { ...team, _goalDiff: goalsFor - goalsAgainst };
    });
    // Sortiere: 1. Punkte, 2. Tordifferenz, 3. Direktvergleich
    const sortedTeams = teamsWithDiff.sort((a, b) => {
      if ((b.mexicano_points || 0) !== (a.mexicano_points || 0))
        return (b.mexicano_points || 0) - (a.mexicano_points || 0);
      if ((b._goalDiff || 0) !== (a._goalDiff || 0))
        return (b._goalDiff || 0) - (a._goalDiff || 0);
      return -computeH2H(a.id, b.id);
    });

    const n = sortedTeams.length;
    const rowPad   = n > 12 ? 'py-0.5 px-2' : n > 8 ? 'py-1 px-2' : 'py-2 px-3';
    const fontSize = n > 12 ? 'text-[clamp(0.6rem,1vw,0.95rem)]' : n > 8 ? 'text-[clamp(0.7rem,1.2vw,1.1rem)]' : 'text-[clamp(0.85rem,1.5vw,1.6rem)]';
    const numFontSize = n > 12 ? 'text-[clamp(0.65rem,1.1vw,1rem)]' : n > 8 ? 'text-[clamp(0.75rem,1.3vw,1.2rem)]' : 'text-[clamp(1rem,1.8vw,1.8rem)]';
    const pktFontSize = n > 12 ? 'text-[clamp(0.65rem,1.1vw,1rem)]' : n > 8 ? 'text-[clamp(0.8rem,1.4vw,1.3rem)]' : 'text-[clamp(1.1rem,1.8vw,2rem)]';
    const colTemplate = 'clamp(1.5rem,2.5vw,3rem) 1fr clamp(2rem,4vw,4.5rem) repeat(3,clamp(1.5rem,2.5vw,3rem)) clamp(2rem,4vw,4rem)';

    return (
      <div className="w-full h-full flex flex-col overflow-hidden min-h-0">
        <h1 className="flex-shrink-0 m-0 mb-2 text-center font-bold leading-tight text-[clamp(1.1rem,2vw,2rem)] text-slate-900 dark:text-white">
          Rangliste
        </h1>
        <div className={`flex-1 min-h-0 rounded-lg overflow-hidden shadow-lg flex flex-col border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200/30'}`}>
          <div className={`flex-shrink-0 grid gap-1 ${rowPad} bg-gradient-to-r from-gold to-gold-dark text-slate-900 font-bold text-xs uppercase tracking-wide`}
               style={{ gridTemplateColumns: colTemplate }}>
            <div className="text-center">#</div>
            <div>Spieler</div>
            <div className="text-center">Pkt</div>
            <div className="text-center">S</div>
            <div className="text-center">N</div>
            <div className="text-center">U</div>
            <div className="text-center">Diff</div>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {sortedTeams.map((team, index) => {
            const teamMatches = matches.filter(m =>
              (m.team1_id === team.id || m.team2_id === team.id ||
               m.team3_id === team.id || m.team4_id === team.id) &&
              m.team1_score !== null && m.team2_score !== null
            );

            let wins = 0, losses = 0, draws = 0, goalsFor = 0, goalsAgainst = 0;
            teamMatches.forEach(match => {
              const isTeam1Side = match.team1_id === team.id || match.team3_id === team.id;
              const t1Score = match.team1_score || 0;
              const t2Score = match.team2_score || 0;
              if (isTeam1Side) {
                goalsFor += t1Score; goalsAgainst += t2Score;
                if (t1Score > t2Score) wins++; else if (t1Score < t2Score) losses++; else draws++;
              } else {
                goalsFor += t2Score; goalsAgainst += t1Score;
                if (t2Score > t1Score) wins++; else if (t2Score < t1Score) losses++; else draws++;
              }
            });
            const goalDiff = goalsFor - goalsAgainst;

            return (
              <div
                key={team.id}
                className={`flex-1 grid gap-1 ${rowPad} ${fontSize} border-b items-center min-h-0
                  ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200/30'}
                  ${index === 0 ? 'bg-gradient-to-r from-amber-100/50 to-transparent border-l-4 border-l-amber-400 dark:from-amber-900/30' : ''}
                  ${index === 1 ? 'bg-gradient-to-r from-slate-200/50 to-transparent border-l-4 border-l-slate-400 dark:from-slate-600/30' : ''}
                  ${index === 2 ? 'bg-gradient-to-r from-orange-100/50 to-transparent border-l-4 border-l-orange-400 dark:from-orange-900/30' : ''}
                `}
                style={{ gridTemplateColumns: colTemplate }}
              >
                <div className={`text-center font-bold ${numFontSize} ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{index + 1}</div>
                <div className={`font-semibold truncate leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{getDisplayName(team)}</div>
                <div className={`text-center font-bold text-gold ${pktFontSize}`}>{team.mexicano_points || 0}</div>
                <div className={`text-center ${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`}>{wins}</div>
                <div className={`text-center ${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`}>{losses}</div>
                <div className={`text-center ${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`}>{draws}</div>
                <div className={`text-center font-bold whitespace-nowrap ${goalDiff > 0 ? 'text-green-500' : goalDiff < 0 ? 'text-red-600' : ''}`}>
                  {goalDiff > 0 ? '+' : ''}{goalDiff}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    );
  };

  // Rendere Gruppentabellen für Groups-Turniere
  const renderGroupStandings = () => {
    if (!tournament || tournament.tournament_type !== 'groups') return null;

    const groups = {};
    teams.forEach(team => {
      if (!groups[team.group_name]) groups[team.group_name] = [];
      groups[team.group_name].push(team);
    });

    return (
      <div className="w-full h-full flex flex-col overflow-hidden min-h-0">
        <h1 className={`flex-shrink-0 m-0 mb-3 text-center font-bold leading-tight text-[clamp(1.3rem,2.5vw,2.5rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Gruppentabellen
        </h1>
        <div className="flex-1 min-h-0 grid gap-4 overflow-y-auto overflow-x-hidden content-start"
             style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(250px,30vw,400px), 1fr))' }}>
          {Object.keys(groups).sort().map(groupName => (
            <div key={groupName} className={`rounded-lg overflow-hidden shadow-lg h-fit border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200/30'}`}>
              <h2 className="m-0 p-3 bg-gradient-to-r from-gold to-gold-dark text-center font-bold text-slate-900 text-[clamp(1rem,1.8vw,1.8rem)]">
                {groupName}
              </h2>
              <div>
                <div className="grid gap-2 p-2 bg-gradient-to-r from-gold to-gold-dark text-slate-900 font-bold text-xs uppercase tracking-wide"
                     style={{ gridTemplateColumns: 'clamp(1.5rem,2vw,2rem) 1fr clamp(2rem,3.5vw,3rem) repeat(2,clamp(1.5rem,2vw,2rem)) clamp(2.8rem,4vw,4rem)' }}>
                  <div className="text-center">#</div>
                  <div>Team</div>
                  <div className="text-center">Pkt</div>
                  <div className="text-center">S</div>
                  <div className="text-center">N</div>
                  <div className="text-center">Diff</div>
                </div>
                {groups[groupName].sort((a, b) => {
                  if ((b.points || 0) !== (a.points || 0)) return (b.points || 0) - (a.points || 0);
                  const diffA = (a.goals_for || 0) - (a.goals_against || 0);
                  const diffB = (b.goals_for || 0) - (b.goals_against || 0);
                  if (diffB !== diffA) return diffB - diffA;
                  return (b.goals_for || 0) - (a.goals_for || 0);
                }).map((team, idx) => {
                  const diff = (team.goals_for || 0) - (team.goals_against || 0);
                  return (
                    <div
                      key={team.id}
                      className={`grid gap-2 p-2 border-b items-center transition-colors text-[clamp(0.85rem,1.5vw,1.6rem)]
                        ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200/30'}
                        ${idx < 2 ? 'bg-green-50 border-l-4 border-l-green-500 dark:bg-green-900/20' : ''}
                      `}
                      style={{ gridTemplateColumns: 'clamp(1.5rem,2vw,2rem) 1fr clamp(2rem,3.5vw,3rem) repeat(2,clamp(1.5rem,2vw,2rem)) clamp(2.8rem,4vw,4rem)' }}
                    >
                      <div className={`text-center font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{idx + 1}</div>
                      <div className={`font-semibold break-words leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{getDisplayName(team)}</div>
                      <div className="text-center font-bold text-gold">{team.points || 0}</div>
                      <div className={`text-center ${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`}>{team.wins || 0}</div>
                      <div className={`text-center ${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`}>{team.losses || 0}</div>
                      <div className={`text-center font-bold whitespace-nowrap ${diff > 0 ? 'text-green-500' : diff < 0 ? 'text-red-600' : ''}`}>
                        {diff > 0 ? '+' : ''}{diff}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Rendere aktuelle Runden-Matches
  // eslint-disable-next-line no-unused-vars
  const renderMatches = () => {
    if (!tournament || !matches.length) return null;

    let currentMatches = [];

    if (tournament.tournament_type === 'mexicano' || tournament.tournament_type === 'americano') {
      // Finde die neueste Runde
      const rounds = {};
      matches.forEach(m => {
        if (m.round && m.round.startsWith('Runde')) {
          const roundNum = parseInt(m.round.replace('Runde ', ''));
          if (!rounds[roundNum]) rounds[roundNum] = [];
          rounds[roundNum].push(m);
        }
      });

      const latestRound = Math.max(...Object.keys(rounds).map(Number));
      currentMatches = rounds[latestRound] || [];
    } else {
      currentMatches = matches.filter(m => m.status === 'offen').slice(0, 8);
    }

    return (
      <div className="w-full h-full flex flex-col overflow-hidden min-h-0">
        <h1 className={`flex-shrink-0 m-0 mb-3 text-center font-bold leading-tight text-[clamp(1.3rem,2.5vw,2.5rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Aktuelle Matches
        </h1>
        <div className="flex-1 min-h-0 grid gap-4 overflow-y-auto overflow-x-hidden content-start"
             style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(250px,28vw,380px), 1fr))' }}>
          {currentMatches.map(match => {
            const team1 = teams.find(t => t.id === match.team1_id);
            const team2 = teams.find(t => t.id === match.team2_id);
            const team3 = teams.find(t => t.id === match.team3_id);
            const team4 = teams.find(t => t.id === match.team4_id);

            const isMexicano = team3 && team4;
            const teamAName = isMexicano
              ? `${getDisplayName(team1)} / ${getDisplayName(team3)}`
              : getDisplayName(team1);
            const teamBName = isMexicano
              ? `${getDisplayName(team2)} / ${getDisplayName(team4)}`
              : getDisplayName(team2);

            return (
              <div
                key={match.id}
                className={`rounded-lg p-4 border-2 border-gold shadow-lg flex flex-col justify-between h-fit min-h-[140px] transition-all hover:-translate-y-0.5 hover:shadow-xl hover:border-gold-dark ${isDarkMode ? 'bg-slate-700' : 'bg-white'}`}
              >
                {match.round && (
                  <div className={`text-center font-semibold mb-1 text-[clamp(0.8rem,1.2vw,1.2rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {match.round}
                  </div>
                )}
                {match.court && (
                  <div className="text-center font-bold text-gold uppercase tracking-widest mb-2 text-[clamp(0.75rem,1.1vw,1.1rem)]">
                    {match.court}
                  </div>
                )}
                <div className="flex flex-col gap-2 mb-3 flex-1 justify-center">
                  <div className={`text-center font-semibold leading-snug text-[clamp(0.95rem,1.6vw,1.6rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {teamAName}
                  </div>
                  <div className={`text-center font-bold tracking-widest my-1 text-[clamp(0.8rem,1.3vw,1.3rem)] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    VS
                  </div>
                  <div className={`text-center font-semibold leading-snug text-[clamp(0.95rem,1.6vw,1.6rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {teamBName}
                  </div>
                </div>
                {match.team1_score !== null && (
                  <div className="flex justify-center items-center gap-4 p-3 bg-gradient-to-r from-gold/20 to-gold/10 rounded-md mt-auto">
                    <span className="font-bold text-gold text-[clamp(1.5rem,3vw,3rem)]">{match.team1_score}</span>
                    <span className={`text-[clamp(1rem,2vw,2rem)] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>:</span>
                    <span className="font-bold text-gold text-[clamp(1.5rem,3vw,3rem)]">{match.team2_score}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Rendere KO-Bracket als echter Turnierbaum
  const renderKOBracket = () => {
    if (!tournament || !matches.length) return null;

    const koMatches = matches.filter(m => !m.round?.includes('Gruppe'));
    if (!koMatches.length) return null;

    return (
      <div className="w-full h-full flex flex-col overflow-hidden min-h-0">
        <h1 className={`flex-shrink-0 m-0 mb-3 text-center font-bold leading-tight text-[clamp(1.2rem,2vw,2.2rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          🏆 Turnierbaum
        </h1>
        <div ref={bracketContainerRef} className="flex-1 min-h-0 overflow-hidden flex justify-center items-start">
          <div
            ref={bracketContentRef}
            style={{
              transform: `scale(${bracketScale})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease',
            }}
          >
            <TournamentBracket
              matches={koMatches}
              teams={teams}
              readOnly={true}
              compact={true}
              isDarkMode={isDarkMode}
              onMatchClick={null}
              scheduleMap={Object.fromEntries((schedule || []).map(s => [s.match_id, s]))}
            />
          </div>
        </div>
      </div>
    );
  };

  // Rendere Runden-Übersicht für Americano/Mexicano
  const renderRoundsOverview = () => {
    if (!tournament || !matches.length) return null;

    // Gruppiere Matches nach Runden
    const rounds = {};
    matches.forEach(m => {
      if (m.round) {
        if (!rounds[m.round]) rounds[m.round] = [];
        rounds[m.round].push(m);
      }
    });

    // Sortiere Runden nach Nummer
    const sortedRoundKeys = Object.keys(rounds).sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, '')) || 0;
      const numB = parseInt(b.replace(/\D/g, '')) || 0;
      return numA - numB;
    });

    return (
      <div className="w-full h-full flex flex-col overflow-hidden min-h-0">
        <h1 className={`flex-shrink-0 m-0 mb-3 text-center font-bold leading-tight text-[clamp(1.3rem,2.5vw,2.5rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Alle Runden
        </h1>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(280px,30vw,400px), 1fr))' }}>
            {sortedRoundKeys.map(roundName => {
              const roundMatches = rounds[roundName];
              const allCompleted = roundMatches.every(m => m.status === 'beendet');

              return (
                <div key={roundName} className={`rounded-lg overflow-hidden shadow-lg border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200/30'}`}>
                  <h2 className={`m-0 p-2 text-center font-bold text-[clamp(0.9rem,1.5vw,1.4rem)] ${allCompleted ? 'bg-green-500 text-white' : 'bg-gradient-to-r from-gold to-gold-dark text-slate-900'}`}>
                    {roundName}
                  </h2>
                  <div className="flex flex-col">
                    {roundMatches.map(match => {
                      const team1 = teams.find(t => t.id === match.team1_id);
                      const team2 = teams.find(t => t.id === match.team2_id);
                      const team3 = teams.find(t => t.id === match.team3_id);
                      const team4 = teams.find(t => t.id === match.team4_id);

                      const isMexicano = team3 && team4;
                      const teamAName = isMexicano
                        ? `${getDisplayName(team1)} / ${getDisplayName(team3)}`
                        : getDisplayName(team1);
                      const teamBName = isMexicano
                        ? `${getDisplayName(team2)} / ${getDisplayName(team4)}`
                        : getDisplayName(team2);

                      const isCompleted = match.status === 'beendet';
                      const hasScore = match.team1_score !== null && match.team2_score !== null;

                      return (
                        <div
                          key={match.id}
                          className={`p-2 border-b last:border-b-0 ${isDarkMode ? 'border-slate-600' : 'border-slate-200/50'} ${isCompleted ? 'bg-green-50/30 dark:bg-green-900/10' : ''}`}
                        >
                          {match.court && (
                            <div className="text-center text-gold font-semibold text-[clamp(0.65rem,0.9vw,0.9rem)] mb-1">
                              {match.court}
                            </div>
                          )}
                          <div className="flex items-center justify-between gap-2 text-[clamp(0.75rem,1.2vw,1.1rem)]">
                            <span className={`flex-1 text-center font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {teamAName}
                            </span>
                            <span className={`flex-shrink-0 font-bold min-w-[3rem] text-center ${hasScore ? 'text-gold' : isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {hasScore ? `${match.team1_score}:${match.team2_score}` : 'vs'}
                            </span>
                            <span className={`flex-1 text-center font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {teamBName}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Rendere Zeitplan
  const renderSchedule = (pageIndex = 0) => {
    // Für Americano/Mexicano: Zeige Runden-Übersicht
    if (tournament?.tournament_type === 'americano' || tournament?.tournament_type === 'mexicano') {
      return renderRoundsOverview();
    }

    // Kein Zeitplan vorhanden: KO → Bracket, sonst nichts
    if (!tournament || !schedule.length) {
      if (tournament?.tournament_type === 'ko_placement') return renderKOBracket();
      return null;
    }

    // Alle Schedule-Einträge mit Match-Daten kombinieren (in Reihenfolge des Backends)
    const allItems = schedule
      .map(scheduleItem => {
        const match = matches.find(m => m.id === scheduleItem.match_id);
        return match ? { scheduleItem, match } : null;
      })
      .filter(Boolean);

    // Auf die aktuelle Seite begrenzen
    const totalPages = Math.max(1, Math.ceil(allItems.length / SCHEDULE_ITEMS_PER_PAGE));
    const safePage = Math.min(pageIndex, totalPages - 1);
    const displaySchedule = allItems.slice(safePage * SCHEDULE_ITEMS_PER_PAGE, (safePage + 1) * SCHEDULE_ITEMS_PER_PAGE);

    const renderScheduleItem = ({ scheduleItem, match }) => {
      const team1 = teams.find(t => t.id === scheduleItem.team1_id);
      const team2 = teams.find(t => t.id === scheduleItem.team2_id);

      // Bestimme das Ergebnis für abgeschlossene Spiele
      let resultText = 'vs';
      if (match.status === 'beendet') {
        if (match.team1_set1 !== null) {
          // Best-of-3: Zeige gewonnene Sätze
          let team1Sets = 0;
          let team2Sets = 0;
          if (match.team1_set1 > match.team2_set1) team1Sets++; else team2Sets++;
          if (match.team1_set2 > match.team2_set2) team1Sets++; else team2Sets++;
          if (match.team1_set3 !== null) {
            if (match.team1_set3 > match.team2_set3) team1Sets++; else team2Sets++;
          }
          resultText = `${team1Sets}:${team2Sets}`;
        } else if (match.team1_score !== null) {
          resultText = `${match.team1_score}:${match.team2_score}`;
        }
      }

      const isCompleted = match.status === 'beendet';
      const isOngoing = match.status === 'läuft';

      return (
        <div
          key={scheduleItem.match_id}
          className={`flex-1 grid gap-2 items-center p-2 rounded-md border-l-[3px] border-b transition-all shadow-sm min-h-0 hover:translate-x-1 hover:shadow-md
            ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white'}
            ${isCompleted ? 'bg-green-50/50 border-l-green-500 dark:bg-green-900/10' : 'border-l-gold border-b-gold/20'}
            ${isOngoing ? 'bg-gold/15 border-l-4' : ''}
          `}
          style={{ gridTemplateColumns: 'clamp(180px,24vw,280px) clamp(50px,7vw,80px) 1fr' }}
        >
          <div className={`font-semibold text-left whitespace-nowrap text-[clamp(0.6rem,0.9vw,1rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {scheduleItem.start_time} - {scheduleItem.round}
          </div>
          <div className="font-semibold text-center text-gold text-[clamp(0.65rem,1vw,1.1rem)]">
            {scheduleItem.court || 'TBD'}
          </div>
          <div className={`flex items-center gap-2 flex-1 min-w-0 text-[clamp(0.65rem,1vw,1.1rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-center">
              {team1 ? getDisplayName(team1) : 'TBD'}
            </span>
            <span className={`font-bold flex-shrink-0 min-w-[clamp(1.8rem,3vw,3rem)] text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {resultText}
            </span>
            <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-center">
              {team2 ? getDisplayName(team2) : 'TBD'}
            </span>
          </div>
        </div>
      );
    };

    return (
      <div className="w-full h-full flex flex-col overflow-hidden min-h-0">
        <div className="flex-shrink-0 flex items-baseline justify-center gap-3 mb-3">
          <h1 className={`m-0 text-center font-bold leading-tight text-[clamp(1.3rem,2.5vw,2.5rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Zeitplan
          </h1>
          {totalPages > 1 && (
            <span className={`text-[clamp(0.75rem,1.2vw,1.1rem)] font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {safePage + 1} / {totalPages}
            </span>
          )}
        </div>
        <div className="flex-1 min-h-0 flex flex-col gap-1 overflow-hidden p-1">
          {displaySchedule.map(item => renderScheduleItem(item))}
        </div>
      </div>
    );
  };

  // Rendere finale Rangliste (nach Turnierbeendigung)
  const renderRankings = () => {
    if (!rankings || !rankings.main || rankings.main.length === 0) {
      return (
        <div className="w-full h-full flex flex-col overflow-hidden min-h-0">
          <h1 className={`flex-shrink-0 m-0 mb-3 text-center font-bold leading-tight text-[clamp(1.3rem,2.5vw,2.5rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Finale Rangliste
          </h1>
          <div className="text-center text-slate-500 p-8 text-[clamp(1.2rem,2vw,2rem)]">
            Rangliste wird noch generiert...
          </div>
        </div>
      );
    }

    // Teile Rankings in zwei Spalten auf
    const halfLength = Math.ceil(rankings.main.length / 2);
    const leftColumn = rankings.main.slice(0, halfLength);
    const rightColumn = rankings.main.slice(halfLength);

    const total = rankings.main.length;
    const rankPad  = total > 12 ? 'py-0.5 px-2' : total > 8 ? 'py-1 px-2' : 'p-2';
    const rankGap  = total > 12 ? 'gap-0.5' : total > 8 ? 'gap-1' : 'gap-2';
    const rankFont = total > 12 ? 'text-[clamp(0.65rem,1.1vw,1rem)]' : total > 8 ? 'text-[clamp(0.75rem,1.3vw,1.2rem)]' : 'text-[clamp(0.8rem,1.5vw,1.5rem)]';
    const rankNumFont = total > 12 ? 'text-[clamp(0.7rem,1.2vw,1.1rem)]' : total > 8 ? 'text-[clamp(0.8rem,1.4vw,1.3rem)]' : 'text-[clamp(1rem,2vw,2rem)]';

    const renderRankItem = (ranking, index) => (
      <div
        key={index}
        className={`flex-1 grid items-center min-h-0 ${rankPad} rounded-md border-l-[3px] shadow-sm
          ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gold/30'}
          ${ranking.place <= 3 ? 'border-l-4 font-semibold' : ''}
          ${ranking.place === 1 ? 'bg-gradient-to-r from-yellow-100/50 to-yellow-50/20 border-l-yellow-400 dark:from-yellow-900/20' : ''}
          ${ranking.place === 2 ? 'bg-gradient-to-r from-slate-200/50 to-slate-100/20 border-l-slate-400 dark:from-slate-600/20' : ''}
          ${ranking.place === 3 ? 'bg-gradient-to-r from-orange-100/50 to-orange-50/20 border-l-orange-400 dark:from-orange-900/20' : ''}
        `}
        style={{ gridTemplateColumns: 'clamp(2rem,6vw,5rem) 1fr' }}
      >
        <div className={`text-center font-bold ${rankNumFont} ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          {ranking.place}.
        </div>
        <div className={`font-semibold text-left overflow-hidden text-ellipsis whitespace-nowrap ${rankFont} ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          {ranking.team}
        </div>
      </div>
    );

    return (
      <div className="w-full h-full flex flex-col overflow-hidden min-h-0">
        <h1 className={`flex-shrink-0 m-0 mb-2 text-center font-bold leading-tight text-[clamp(1.1rem,2vw,2rem)] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Finale Rangliste
        </h1>
        <div className="flex-1 min-h-0 overflow-hidden grid grid-cols-2 gap-3">
          <div className={`flex flex-col ${rankGap} overflow-hidden`}>
            {leftColumn.map((ranking, index) => renderRankItem(ranking, index))}
          </div>
          <div className={`flex flex-col ${rankGap} overflow-hidden`}>
            {rightColumn.map((ranking, index) => renderRankItem(ranking, index))}
          </div>
        </div>
      </div>
    );
  };

  // Zeige Ladebildschirm nur wenn noch nicht geladen
  if (!tournamentsLoaded) {
    return (
      <div className={`fixed inset-0 w-screen h-screen overflow-hidden flex flex-col font-sans ${isDarkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-slate-200' : 'bg-gradient-to-br from-slate-50 to-white text-slate-900'}`}>
        <div className="flex justify-center items-center h-screen text-gold text-[clamp(1.3rem,2.2vw,2.5rem)]">
          Lade Turnierdaten...
        </div>
      </div>
    );
  }

  // Zeige Meldung wenn keine aktiven Turniere vorhanden
  if (tournamentsLoaded && activeTournaments.length === 0) {
    return (
      <div className={`fixed inset-0 w-screen h-screen overflow-hidden flex flex-col font-sans ${isDarkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-slate-200' : 'bg-gradient-to-br from-slate-50 to-white text-slate-900'}`}>
        <div className="flex flex-col justify-center items-center h-screen gap-4">
          <div className="text-gold text-[clamp(1.5rem,3vw,3rem)] font-bold">
            Keine aktiven Turniere
          </div>
          <div className={`text-[clamp(1rem,1.5vw,1.5rem)] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Momentan finden keine Turniere statt
          </div>
        </div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className={`fixed inset-0 w-screen h-screen overflow-hidden flex flex-col font-sans ${isDarkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-slate-200' : 'bg-gradient-to-br from-slate-50 to-white text-slate-900'}`}>
        <div className="flex justify-center items-center h-screen text-gold text-[clamp(1.3rem,2.2vw,2.5rem)]">
          {error || 'Turnierdaten werden geladen...'}
        </div>
      </div>
    );
  }

  // Hilfsfunktion für Datumsformatierung
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch {
      return '';
    }
  };

  return (
    <div className={`fixed inset-0 w-screen h-screen overflow-hidden flex flex-col font-sans ${isDarkMode ? 'bg-gradient-to-br from-slate-800 to-slate-900 text-slate-200' : 'bg-gradient-to-br from-slate-50 to-white text-slate-900'}`}>
      {/* Header */}
      <header className={`flex-shrink-0 min-h-[clamp(60px,8vh,100px)] px-4 py-2 border-b-[3px] border-gold flex items-center shadow-md ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
        {/* Left: Haupt-Logo + Sponsor */}
        <div className="flex-1 flex justify-start items-center gap-3">
          <img
            src={logoUrl}
            alt="Logo"
            className="h-[clamp(22px,4.5vh,50px)] w-auto max-w-[clamp(80px,10vw,160px)] object-contain flex-shrink-0"
          />
          {currentSponsor && (
            <>
              <span className={`italic whitespace-nowrap text-[clamp(0.5rem,0.8vw,0.8rem)] flex-shrink-0 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                powered by
              </span>
              {currentSponsor.link ? (
                <a href={currentSponsor.link} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img
                    src={currentSponsor.logo_url}
                    alt={currentSponsor.name || 'Sponsor'}
                    className="h-[clamp(22px,4.5vh,50px)] w-auto max-w-[clamp(80px,10vw,160px)] object-contain opacity-90"
                  />
                </a>
              ) : (
                <img
                  src={currentSponsor.logo_url}
                  alt={currentSponsor.name || 'Sponsor'}
                  className="h-[clamp(22px,4.5vh,50px)] w-auto max-w-[clamp(80px,10vw,160px)] object-contain opacity-90 flex-shrink-0"
                />
              )}
            </>
          )}
        </div>
        {/* Center: Turnier-Info */}
        <div className="flex-shrink-0 text-center">
          <h1 className={`m-0 font-bold tracking-wide leading-snug text-[clamp(1rem,2.5vw,2.5rem)] break-words ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {tournament.name}
          </h1>
          <div className={`mt-1 tracking-wider leading-none text-[clamp(0.75rem,1.2vw,1.2rem)] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {formatDate(tournament.date) && `${formatDate(tournament.date)} - `}{tournament.num_teams} Teilnehmer
          </div>
        </div>
        {/* Right spacer für symmetrisches Zentrieren */}
        <div className="flex-1" />
      </header>

      {/* Top-right: QR Code + WebSocket Status */}
      <div className="absolute top-3 right-4 z-50 flex items-center gap-3">
        {/* QR Code + Label + Pfeil */}
        <div className="flex items-center gap-2">
          <div className={`text-right leading-snug text-[clamp(0.6rem,0.9vw,0.85rem)] font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            {qrLangDe ? <>Live Scores<br />auf dem<br />Smartphone</> : <>Live Scores<br />on your<br />Smartphone</>}
          </div>
          {/* Bleistift-Pfeil als Bild */}
          <img
            src="/pfeil.png"
            alt=""
            style={{
              width: 'clamp(60px, 7vw, 100px)',
              flexShrink: 0,
              alignSelf: 'center',
              transform: 'scaleX(-1)',
              mixBlendMode: isDarkMode ? 'screen' : 'multiply',
              filter: isDarkMode ? 'invert(1) brightness(1.8)' : 'none',
              opacity: isDarkMode ? 0.85 : 0.7,
            }}
          />
          <div className={`p-1.5 rounded-lg border shadow-md ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}>
            <QRCodeSVG
              value={qrUrl}
              size={Math.round(Math.min(Math.max(window.innerHeight * 0.09, 68), 96))}
              bgColor={isDarkMode ? '#334155' : '#ffffff'}
              fgColor={isDarkMode ? '#f1f5f9' : '#0f172a'}
              level="M"
            />
          </div>
        </div>
        {/* WebSocket Connection Status */}
        <div
          title={wsConnected ? 'Live' : 'Offline'}
          className={`w-4 h-4 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}
        />
      </div>

      {/* Content */}
      <div className={`flex-1 min-h-0 p-4 overflow-hidden flex flex-col opacity-0 ${fadeClass === 'fade-in' ? 'animate-fade-in' : fadeClass === 'fade-out' ? 'animate-fade-out' : ''}`}>
        {currentView === 'standings' && renderStandings()}
        {currentView === 'bracket' && renderKOBracket()}
        {currentView.startsWith('schedule_') && renderSchedule(parseInt(currentView.split('_')[1]) || 0)}
        {currentView === 'rankings' && renderRankings()}
      </div>

      {/* Footer */}
      <footer className={`relative flex-shrink-0 h-[clamp(40px,5vh,60px)] px-4 py-2 border-t-2 border-gold/30 flex justify-between items-center overflow-hidden shadow-[0_-2px_8px_rgba(0,0,0,0.05)] ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="flex items-center gap-2 h-full">
          <img
            src={logoUrl}
            alt="Logo"
            className="h-[clamp(20px,3vh,35px)] w-auto object-contain flex-shrink-0"
          />
          {currentSponsor && (
            <>
              <span className={`italic whitespace-nowrap text-[clamp(0.5rem,0.8vw,0.75rem)] flex-shrink-0 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                powered by
              </span>
              {currentSponsor.link ? (
                <a href={currentSponsor.link} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src={currentSponsor.logo_url} alt={currentSponsor.name || 'Sponsor'} className="h-[clamp(20px,3vh,35px)] w-auto object-contain opacity-90" />
                </a>
              ) : (
                <img src={currentSponsor.logo_url} alt={currentSponsor.name || 'Sponsor'} className="h-[clamp(20px,3vh,35px)] w-auto object-contain opacity-90 flex-shrink-0" />
              )}
            </>
          )}
        </div>
        <div className="flex gap-2">
          {(() => {
            const isGroups = tournament?.tournament_type === 'groups';
            const hasKO = isGroups && tournament?.group_phase_completed === 1;
            const isDone = tournament?.is_completed === 1;
            const pageCount = schedulePageCountRef.current;
            const schedulePages = Array.from({ length: pageCount }, (_, i) => `schedule_${i}`);
            const views = isDone
              ? (hasKO ? ['standings', 'bracket', ...schedulePages, 'rankings'] : ['standings', ...schedulePages, 'rankings'])
              : (hasKO ? ['standings', 'bracket', ...schedulePages] : ['standings', ...schedulePages]);
            return views.map(v => (
              <span key={v} className={`w-[clamp(0.6rem,1vw,1rem)] h-[clamp(0.6rem,1vw,1rem)] rounded-full transition-all ${currentView === v ? 'bg-gold shadow-[0_0_8px_rgba(232,183,58,0.5)]' : 'bg-slate-500/30'}`} />
            ));
          })()}
        </div>
        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-[7px] bg-slate-300/40 overflow-hidden z-10">
          <div
            className="h-full bg-gradient-to-r from-gold to-gold-dark transition-[width] duration-100 shadow-[0_2px_8px_rgba(232,183,58,0.6)]"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </footer>

    </div>
  );
}

export default PresentationView;
